/**
 * PomodoroWidget · floating bottom-right FAB · slot_pomodoro_rework PR.3-PR.7 (17 MAY 2026).
 *
 * Visual layer: Б · Ink bloom Riso (Genie PR.7 pick) · brutalist drop shadow +
 * paper/peach/teal-soft phase fills + Fraunces italic timer + Space Mono labels.
 * Hand-drawn SVG iconography:
 *   - TomatoIcon 1B · Riso offset (color fill displaced from ink outline)
 *   - SlidersIcon G2 · mixing console metaphor с accent knobs
 *
 * Viewport-aware layout (per Genie PR.7 mobile pick · Option B compact round):
 *   - desktop (>1100px): full pill + adjacent square settings · bottom:16, right:16
 *   - mobile/tablet (<=1100px): vertical stack · small 30px settings circle above
 *     · 52px round play/pause main с outer progress ring · lifted bottom:76 (above
 *     BottomTabs 60px + 16 gap) · today-count corner badge
 *
 * Z-index ordering (respects ADR-010):
 *   Sidebar z=30 < AtelierOverlay z=40 < PomodoroWidget z=42 <
 *   PomodoroSettings z=43 < Topbar z=50 < Reflection modal z=70
 *
 * Visibility:
 *   - HIDDEN_NAV_SCREENS (login / welcome / finaltest / auth flows) → null
 *
 * Phase states (both layouts):
 *   - idle: paper bg · ink text · «25м» Fraunces italic
 *   - work running: peach bg · paper text · tomato inverted · accent-deep progress
 *   - short/long break: teal-soft bg · teal-deep text · tomato standard · teal-deep progress
 *
 * Settings affordance · dedicated SlidersIcon button (fixes Genie's «исчезла кнопка
 * настроек помодоро» complaint 17 MAY · gear replaced с sliders Genie pick G2).
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePomodoro, DEFAULT_POMO_SETTINGS } from '@/hooks/usePomodoro';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { HIDDEN_NAV_SCREENS } from './Sidebar';
import { PomodoroSettings } from './PomodoroSettings';

const Z_INDEX_WIDGET = 42;
const Z_INDEX_REFLECTION = 70;

/**
 * Mobile breakpoint · matches NavShell MOBILE_BREAKPOINT_PX (1100px).
 * <=1100px viewport renders BottomTabs · widget switches к compact round mode +
 * lifts above tabs (76px = 60 tabs + 16 gap). Per Genie PR.7 mobile pick 17 MAY 2026.
 */
const MOBILE_BREAKPOINT_PX = 1100;
const FAB_LIFT_MOBILE_PX = 76;
/**
 * Higher lift on the ticket screen (mobile nav batch · 6 JUN 2026). The ticket
 * floating action-bar (globals.css `.ticket-action-bar`) now sits flush on top
 * of BottomTabs (`bottom: --bottom-tabs-h` ≈ 72px) and is ~56px tall, so its top
 * edge is ~128px up — full-width, with a `→ next` button under the FAB's right
 * corner. The default 76px lift dropped the orange FAB right on top of that nav
 * button. Lifting the FAB above the action-bar clears it while keeping the focus
 * timer reachable (hiding it would remove the timer exactly where studying
 * happens). Value = nav(72) + action-bar(56) + 8px gap. Only the ticket screen
 * renders the action-bar.
 */
const FAB_LIFT_MOBILE_TICKET_PX = 136;
const FAB_LIFT_DESKTOP_PX = 16;

/** Portal helper · escapes containing blocks (Topbar's backdrop-filter etc.). */
function PortalToBody({ children }: { children: React.ReactNode }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

/** Screen-reader-only style (preserves testid + a11y · invisible visually). */
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface PomodoroWidgetProps {
  /** Current screen name · widget hides на HIDDEN_NAV_SCREENS (auth flows). */
  screen: string;
}

export function PomodoroWidget({ screen }: PomodoroWidgetProps) {
  const pomo = usePomodoro();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reflectionOpen, setReflectionOpen] = useState(false);

  // PR.7 mobile · viewport-aware layout. <=1100px → compact round + lifted above BottomTabs.
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT_PX;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-show reflection on transition к break с pendingReflection.
  useEffect(() => {
    if (pomo.pendingReflection && (pomo.phase === 'short-break' || pomo.phase === 'long-break')) {
      setReflectionOpen(true);
    }
  }, [pomo.pendingReflection, pomo.phase]);

  if (HIDDEN_NAV_SCREENS.has(screen)) return null;

  const isBreak = pomo.phase === 'short-break' || pomo.phase === 'long-break';
  const isWorkRunning = pomo.phase === 'work' && pomo.running;
  const phaseLabel = pomo.phase === 'work'
    ? 'фокус'
    : pomo.phase === 'short-break'
      ? 'перерыв'
      : pomo.phase === 'long-break'
        ? 'долгий перерыв'
        : 'idle';

  const onPillClick = () => {
    if (pomo.phase === 'idle' && !pomo.running) pomo.start();
    else if (pomo.running) pomo.pause();
    else pomo.start();
  };

  const pillTimeText = pomo.phase === 'idle'
    ? `${pomo.settings.workMin}м`
    : pomo.display;

  const tooltip = pomo.running
    ? `Pomodoro · ${phaseLabel} · ${pomo.display} · клик для паузы`
    : pomo.phase === 'idle'
      ? `Pomodoro · клик чтобы запустить ${pomo.settings.workMin} мин фокуса`
      : `Pomodoro · пауза · клик чтобы продолжить`;

  const onPillKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onPillClick();
    }
  };

  // Phase-driven theme tokens (CSS vars from globals.css · fallbacks для safety).
  const pillBg = isWorkRunning
    ? 'var(--peach, #F26B4A)'
    : isBreak
      ? 'var(--teal-soft, #B6DCE5)'
      : 'var(--paper, #fffbf5)';
  const pillFg = isWorkRunning
    ? 'var(--paper, #fffbf5)'
    : isBreak
      ? 'var(--teal-deep, #1B6378)'
      : 'var(--ink, #1c1a18)';
  const arcColor = isBreak
    ? 'var(--teal-deep, #1B6378)'
    : 'var(--accent-deep, #B73A1E)';
  const tomatoVariant: 'standard' | 'inverted' = isWorkRunning ? 'inverted' : 'standard';

  // Mobile FAB · compact round (52px main + 30px settings stacked above). Lifted
  // above BottomTabs (76px); on the ticket screen lifted higher to clear the
  // floating action-bar (see FAB_LIFT_MOBILE_TICKET_PX).
  const mobileBottom = screen === 'ticket' ? FAB_LIFT_MOBILE_TICKET_PX : FAB_LIFT_MOBILE_PX;
  const mobileFAB = (
    <div
      className="pomo-widget-fab pomo-widget-fab--mobile"
      data-testid="pomo-widget-fab"
      style={{
        position: 'fixed',
        bottom: mobileBottom,
        right: 16,
        zIndex: Z_INDEX_WIDGET,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
      }}
    >
      <button
        type="button"
        className="pomo-settings-btn pomo-settings-btn--mobile"
        data-testid="pomo-settings-btn"
        data-pomo-settings-toggle="true"
        onClick={() => setSettingsOpen((open) => !open)}
        title="Настройки Pomodoro"
        aria-label="Настройки Pomodoro"
        aria-expanded={settingsOpen}
        aria-controls="pomo-settings-popup"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          background: 'var(--paper, #fffbf5)',
          color: 'var(--ink, #1c1a18)',
          border: '1.5px solid var(--ink, #1c1a18)',
          borderRadius: '50%',
          boxShadow: '2px 2px 0 var(--ink, #1c1a18)',
          cursor: 'pointer',
        }}
      >
        <SlidersIcon size={14} />
      </button>
      <button
        type="button"
        className="pomo-pill pomo-pill--mobile"
        data-testid="pomo-pill"
        onClick={onPillClick}
        onKeyDown={onPillKeyDown}
        title={tooltip}
        aria-label={tooltip}
        aria-pressed={pomo.running}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 52,
          height: 52,
          padding: 0,
          background: pillBg,
          color: pillFg,
          border: '1.5px solid var(--ink, #1c1a18)',
          borderRadius: '50%',
          boxShadow: '3px 3px 0 var(--ink, #1c1a18)',
          cursor: 'pointer',
          transition: 'background 200ms ease, color 200ms ease',
          overflow: 'visible',
        }}
      >
        <TomatoIcon variant={tomatoVariant} size={28} />
        {/* Outer progress ring · only running · stroke-dasharray на circle с rotation -90° */}
        {pomo.running && pomo.phase !== 'idle' && (
          <svg
            viewBox="0 0 56 56"
            width="56"
            height="56"
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -3.5,
              left: -3.5,
              pointerEvents: 'none',
            }}
          >
            <circle
              cx="28"
              cy="28"
              r="26.5"
              fill="none"
              stroke={arcColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 26.5 * Math.max(0, Math.min(1, pomo.progress))} ${2 * Math.PI * 26.5}`}
              transform="rotate(-90 28 28)"
              style={{ transition: 'stroke-dasharray 1s linear' }}
            />
          </svg>
        )}
        {/* SR-only label · preserves testid + screen reader accessibility. */}
        <span data-testid="pomo-pill-label" style={srOnlyStyle}>
          {pillTimeText}
          {pomo.phase !== 'idle' && (
            <span>{` · ${phaseLabel}`}</span>
          )}
        </span>
        {pomo.todayCount > 0 && (
          <span
            data-testid="pomo-today-count"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              background: 'var(--ink, #1c1a18)',
              color: 'var(--paper, #fffbf5)',
              borderRadius: 9,
              fontSize: 10,
              fontFamily: '"Space Mono", monospace',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              border: '1px solid var(--paper, #fffbf5)',
            }}
          >
            {pomo.todayCount}
          </span>
        )}
        {/* SR-only progress dial element · preserves testid backward compat. */}
        <span
          data-testid="pomo-radial-dial"
          aria-hidden="true"
          style={srOnlyStyle}
        />
      </button>
    </div>
  );

  // Desktop FAB · full pill + adjacent square settings · bottom:16.
  const desktopFAB = (
    <div
      className="pomo-widget-fab"
      data-testid="pomo-widget-fab"
      style={{
        position: 'fixed',
        bottom: FAB_LIFT_DESKTOP_PX,
        right: 16,
        zIndex: Z_INDEX_WIDGET,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <button
        type="button"
        className="pomo-pill"
        data-testid="pomo-pill"
        onClick={onPillClick}
        onKeyDown={onPillKeyDown}
        title={tooltip}
        aria-label={tooltip}
        aria-pressed={pomo.running}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 14px 7px 10px',
          minWidth: 144,
          background: pillBg,
          color: pillFg,
          border: '1.5px solid var(--ink, #1c1a18)',
          borderRadius: 6,
          boxShadow: '3px 3px 0 var(--ink, #1c1a18)',
          fontFamily: 'var(--ff-display, Fraunces), serif',
          fontStyle: 'italic',
          fontSize: 15,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'background 200ms ease, color 200ms ease',
          overflow: 'hidden',
        }}
      >
        <TomatoIcon variant={tomatoVariant} size={26} />
        <span
          data-testid="pomo-pill-label"
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 6,
            lineHeight: 1,
          }}
        >
          {pillTimeText}
          {pomo.phase !== 'idle' && (
            <span
              style={{
                fontFamily: '"Space Mono", monospace',
                fontStyle: 'normal',
                fontSize: 10,
                fontWeight: 400,
                opacity: 0.85,
                letterSpacing: 0.2,
              }}
            >{` · ${phaseLabel}`}</span>
          )}
        </span>
        {pomo.todayCount > 0 && (
          <span
            data-testid="pomo-today-count"
            style={{
              fontFamily: '"Space Mono", monospace',
              fontStyle: 'normal',
              fontSize: 10,
              opacity: 0.75,
              marginLeft: 'auto',
              paddingLeft: 4,
            }}
          >
            · {pomo.todayCount}
          </span>
        )}
        {/* Bottom progress accent · Riso stripe · only running. */}
        <span
          data-testid="pomo-radial-dial"
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: 2.5,
            width: pomo.running && pomo.phase !== 'idle'
              ? `${Math.max(0, Math.min(1, pomo.progress)) * 100}%`
              : 0,
            background: arcColor,
            transition: 'width 1s linear',
            pointerEvents: 'none',
          }}
        />
      </button>
      <button
        type="button"
        className="pomo-settings-btn"
        data-testid="pomo-settings-btn"
        data-pomo-settings-toggle="true"
        onClick={() => setSettingsOpen((open) => !open)}
        title="Настройки Pomodoro"
        aria-label="Настройки Pomodoro"
        aria-expanded={settingsOpen}
        aria-controls="pomo-settings-popup"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          background: 'var(--paper, #fffbf5)',
          color: 'var(--ink, #1c1a18)',
          border: '1.5px solid var(--ink, #1c1a18)',
          borderRadius: 6,
          boxShadow: '3px 3px 0 var(--ink, #1c1a18)',
          cursor: 'pointer',
          transition: 'transform 120ms ease',
        }}
      >
        <SlidersIcon size={20} />
      </button>
    </div>
  );

  return (
    <>
      {isMobile ? mobileFAB : desktopFAB}

      {settingsOpen && (
        <PomodoroSettings
          settings={pomo.settings}
          onChange={(next) => pomo.setSettings(next)}
          onResetDefaults={() => pomo.setSettings(DEFAULT_POMO_SETTINGS)}
          onResetTimer={() => pomo.reset()}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {reflectionOpen && (
        <PortalToBody>
          <PomodoroReflectionPanel
            onSave={(text) => {
              pomo.acknowledgeReflection();
              setReflectionOpen(false);
              void text;
            }}
            onSkip={() => {
              pomo.acknowledgeReflection();
              setReflectionOpen(false);
            }}
          />
        </PortalToBody>
      )}
    </>
  );
}

// ============================================================================
// Hand-drawn Riso iconography · slot_pomodoro_rework PR.7 (17 MAY 2026).
// ============================================================================

interface TomatoIconProps {
  /** 'standard' · ink outline + accent fill · 'inverted' · paper outline + paper fill (для accent bg). */
  variant?: 'standard' | 'inverted';
  size?: number;
}

/**
 * Pomodoro tomato · Riso 1B · color fill offset 2.5px down-right от ink outline.
 * Imitates risograph print registration imperfection · hand-printed feel.
 */
function TomatoIcon({ variant = 'standard', size = 26 }: TomatoIconProps) {
  const isInverted = variant === 'inverted';
  const strokeColor = isInverted ? 'var(--paper, #fffbf5)' : 'var(--ink, #1c1a18)';
  const fillColor = isInverted
    ? 'var(--paper, #fffbf5)'
    : 'var(--accent, #d96a4a)';
  const fillOpacity = isInverted ? 0.35 : 1;
  const leafFill = isInverted ? 'var(--paper, #fffbf5)' : 'var(--teal, #2E8AA0)';
  const leafOpacity = isInverted ? 0.45 : 1;

  return (
    <svg
      width={size}
      height={size}
      viewBox="-26 -28 52 56"
      aria-hidden="true"
      data-testid="pomo-tomato-icon"
      style={{ flexShrink: 0 }}
    >
      {/* Color fill offset 2.5px down-right (Riso registration imperfection) */}
      <path
        d="M 2 -13 C 19 -11, 25 0, 23 10 C 21 19, 13 23, 2 23 C -10 23, -18 20, -20 11 C -22 0, -16 -11, 2 -13 Z"
        fill={fillColor}
        fillOpacity={fillOpacity}
      />
      {/* Ink outline · larger · base position */}
      <path
        d="M 0 -16 C 18 -14, 24 -2, 22 8 C 20 18, 12 22, 0 22 C -12 22, -20 18, -22 8 C -24 -2, -18 -14, 0 -16 Z"
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Leaf 1 · color offset */}
      <path
        d="M -1 -19 Q 1 -25 6 -22 L 7 -19 Q 5 -16 0 -16 Z"
        fill={leafFill}
        fillOpacity={leafOpacity}
      />
      <path
        d="M -3 -22 Q -1 -27 4 -25 L 5 -22 Q 3 -19 -2 -19 Z"
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Leaf 2 · second cluster · color offset */}
      <path
        d="M 4 -19 Q 11 -22 11 -16 L 9 -15 Q 5 -15 4 -17 Z"
        fill={leafFill}
        fillOpacity={leafOpacity * 0.85}
      />
      <path
        d="M 2 -22 Q 9 -25 9 -19 L 7 -18 Q 4 -18 2 -20 Z"
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Stem */}
      <line
        x1="2"
        y1="-19"
        x2="3"
        y2="-23"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SlidersIconProps {
  size?: number;
}

/**
 * Settings · G2 mixing console · 3 horizontal sliders с accent knobs offset.
 * «Tweak» affordance чище gear · matches Riso brutalist aesthetic.
 */
function SlidersIcon({ size = 20 }: SlidersIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-20 -18 40 36"
      aria-hidden="true"
      data-testid="pomo-sliders-icon"
      style={{ flexShrink: 0 }}
    >
      <rect x="-15.5" y="-12.5" width="31" height="2.5" fill="var(--accent-soft, #fbe5d4)" />
      <rect x="-15.5" y="-1.5" width="31" height="2.5" fill="var(--accent-soft, #fbe5d4)" />
      <rect x="-15.5" y="9.5" width="31" height="2.5" fill="var(--accent-soft, #fbe5d4)" />
      <line x1="-16" y1="-13" x2="16" y2="-13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="-16" y1="-2" x2="16" y2="-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="-16" y1="9" x2="16" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="5" cy="-13" r="4" fill="var(--accent, #d96a4a)" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="-6" cy="-2" r="4" fill="var(--accent, #d96a4a)" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="9" cy="9" r="4" fill="var(--accent, #d96a4a)" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

// ============================================================================
// Reflection modal · preserved функционально · brutalist shadow polish.
// ============================================================================

interface PomodoroReflectionPanelProps {
  onSave: (text: string) => void;
  onSkip: () => void;
}

function PomodoroReflectionPanel({ onSave, onSkip }: PomodoroReflectionPanelProps) {
  const [text, setText] = useState('');
  const cardRef = useFocusTrap<HTMLDivElement>(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSkip]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pomo-reflection-title"
      data-testid="pomo-reflection-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'color-mix(in srgb, var(--ink, #1c1a18) 45%, transparent)',
        zIndex: Z_INDEX_REFLECTION,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={cardRef}
        style={{
          position: 'relative',
          background: 'var(--paper, #fffbf5)',
          border: '1.5px solid var(--ink, #1c1a18)',
          borderRadius: 8,
          boxShadow: '5px 5px 0 var(--peach, #F26B4A)',
          padding: '24px 28px',
          width: 440,
          maxWidth: 'calc(100vw - 40px)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <TomatoIcon variant="standard" size={36} />
          <h2
            id="pomo-reflection-title"
            style={{
              fontFamily: 'var(--ff-display, Fraunces), serif',
              fontStyle: 'italic',
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--ink, #1c1a18)',
              margin: 0,
            }}
          >
            Pomo завершён
          </h2>
        </div>
        <p
          style={{
            fontSize: 13.5,
            color: 'var(--ink-soft, #4a4642)',
            marginTop: 8,
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          Что делала в фокусе? Одно предложение. Можно пропустить — серебряные пули не нужны.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Например: разбирала Story 2 AC, нашла 3 негативных кейса"
          rows={3}
          style={{
            width: '100%',
            fontFamily: 'Inter, sans-serif',
            fontSize: 13.5,
            padding: 10,
            border: '1.2px solid var(--ink, #1c1a18)',
            borderRadius: 4,
            background: 'var(--surface-2, #f7f3ec)',
            color: 'var(--ink, #1c1a18)',
          }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="cta-ghost" onClick={onSkip}>
            Пропустить
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={text.trim().length === 0}
            onClick={() => onSave(text.trim())}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
